import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2 } from 'lucide-react';
import { Button, Input, TextArea } from '../shared';
import { VENDOR_PAGE_ACCESS_GROUPS, VENDOR_PAGE_ACCESS_OPTIONS } from '../../utils/vendorPlanAccess';

const CreateSubscriptionModal = ({
  isOpen,
  onClose,
  onSave,
  subscription = null,
  isEdit = false,
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    features_list: [''],
    price_monthly: '',
    price_yearly: '',
    currency: 'USD',
    max_bookings_per_month: '',
    max_services: '',
    max_portfolio_items: '',
    max_images: '',
    max_packages_per_service: '',
    feature_analytics: false,
    feature_custom_branding: false,
    feature_priority_support: false,
    feature_api_access: false,
    feature_advanced_booking: false,
    feature_multi_location: false,
    is_active: true,
    sort_order: 0,
    stripe_product_id: '',
    stripe_price_id_monthly: '',
    stripe_price_id_yearly: '',
    vendor_page_access: VENDOR_PAGE_ACCESS_OPTIONS.map((option) => option.key)
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset local loading state when external loading state changes
  useEffect(() => {
    if (!isLoading) {
      setIsSubmitting(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (isEdit && subscription) {
      setFormData({
        name: subscription.name || '',
        description: subscription.description || '',
        features_list: subscription.features_list && subscription.features_list.length > 0 
          ? subscription.features_list 
          : [''],
        price_monthly:
          subscription.price_monthly != null ? subscription.price_monthly : subscription.price || '',
        price_yearly: subscription.price_yearly != null ? subscription.price_yearly : '',
        currency: subscription.currency || 'USD',
        max_bookings_per_month: subscription.max_bookings_per_month || '',
        max_services: subscription.max_services || '',
        max_portfolio_items: subscription.max_portfolio_items || '',
        max_images: subscription.max_images || '',
        feature_analytics: subscription.feature_analytics || false,
        feature_custom_branding: subscription.feature_custom_branding || false,
        feature_priority_support: subscription.feature_priority_support || false,
        feature_api_access: subscription.feature_api_access || false,
        feature_advanced_booking: subscription.feature_advanced_booking || false,
        feature_multi_location: subscription.feature_multi_location || false,
        is_active: subscription.is_active !== undefined ? subscription.is_active : true,
        sort_order: subscription.sort_order || 0,
        stripe_product_id: subscription.stripe_product_id || '',
        stripe_price_id_monthly:
          subscription.stripe_price_id_monthly || subscription.stripe_price_id || '',
        stripe_price_id_yearly: subscription.stripe_price_id_yearly || '',
        vendor_page_access: Array.isArray(subscription.vendor_page_access)
          ? subscription.vendor_page_access
          : VENDOR_PAGE_ACCESS_OPTIONS.map((option) => option.key)
      });
    }
  }, [isEdit, subscription]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features_list];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features_list: newFeatures
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features_list: [...prev.features_list, '']
    }));
  };

  const toggleAccess = (accessKey) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.vendor_page_access) ? prev.vendor_page_access : [];
      const next = current.includes(accessKey)
        ? current.filter((key) => key !== accessKey)
        : [...current, accessKey];
      return {
        ...prev,
        vendor_page_access: next,
      };
    });
  };

  const removeFeature = (index) => {
    if (formData.features_list.length > 1) {
      const newFeatures = formData.features_list.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        features_list: newFeatures
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting || isLoading) {
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      // Validation
      const newErrors = {};
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      const pm = parseFloat(formData.price_monthly);
      const py = parseFloat(formData.price_yearly);
      if (
        formData.price_monthly === '' ||
        formData.price_monthly === null ||
        formData.price_monthly === undefined ||
        Number.isNaN(pm) ||
        pm < 0
      ) {
        newErrors.price_monthly = 'Monthly price is required (0 or greater)';
      }
      if (
        formData.price_yearly === '' ||
        formData.price_yearly === null ||
        formData.price_yearly === undefined ||
        Number.isNaN(py) ||
        py < 0
      ) {
        newErrors.price_yearly = 'Yearly price is required (0 or greater)';
      }
      
      // Filter out empty features
      const filteredFeatures = formData.features_list.filter(feature => feature.trim() !== '');
      if (filteredFeatures.length === 0) {
        newErrors.features_list = 'At least one feature is required';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setIsSubmitting(false);
        return;
      }

      const stripePricePattern = /^price_[A-Za-z0-9]{14,}$/;
      const stripeProductPattern = /^prod_[A-Za-z0-9]{8,}$/;
      if (
        formData.stripe_price_id_monthly?.trim() &&
        !stripePricePattern.test(formData.stripe_price_id_monthly.trim())
      ) {
        newErrors.stripe_price_id_monthly =
          'Paste the full Stripe Price ID (starts with price_, ~24+ characters). Copy from Stripe → Product → Pricing.';
      }
      if (
        formData.stripe_price_id_yearly?.trim() &&
        !stripePricePattern.test(formData.stripe_price_id_yearly.trim())
      ) {
        newErrors.stripe_price_id_yearly =
          'Paste the full Stripe Price ID (starts with price_, ~24+ characters). Copy from Stripe → Product → Pricing.';
      }
      if (
        formData.stripe_product_id?.trim() &&
        !stripeProductPattern.test(formData.stripe_product_id.trim())
      ) {
        newErrors.stripe_product_id =
          'Paste the full Stripe Product ID (starts with prod_).';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setIsSubmitting(false);
        return;
      }

      // Prepare data for submission
      const submitData = {
        ...formData,
        price_monthly: parseFloat(formData.price_monthly),
        price_yearly: parseFloat(formData.price_yearly),
        max_bookings_per_month: formData.max_bookings_per_month ? parseInt(formData.max_bookings_per_month) : null,
        max_services: formData.max_services ? parseInt(formData.max_services) : null,
        max_portfolio_items: formData.max_portfolio_items ? parseInt(formData.max_portfolio_items) : null,
        max_images: formData.max_images ? parseInt(formData.max_images) : null,
        max_packages_per_service: formData.max_packages_per_service ? parseInt(formData.max_packages_per_service) : null,
        features_list: filteredFeatures,
        sort_order: parseInt(formData.sort_order),
        stripe_product_id: formData.stripe_product_id?.trim() || null,
        stripe_price_id_monthly: formData.stripe_price_id_monthly?.trim() || null,
        stripe_price_id_yearly: formData.stripe_price_id_yearly?.trim() || null,
        vendor_page_access: formData.vendor_page_access
      };

      await onSave(submitData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      features_list: [''],
      price_monthly: '',
      price_yearly: '',
      currency: 'USD',
      max_bookings_per_month: '',
      max_services: '',
      max_portfolio_items: '',
      max_images: '',
      max_packages_per_service: '',
      feature_analytics: false,
      feature_custom_branding: false,
      feature_priority_support: false,
      feature_api_access: false,
      feature_advanced_booking: false,
      feature_multi_location: false,
      is_active: true,
      sort_order: 0,
      stripe_product_id: '',
      stripe_price_id_monthly: '',
      stripe_price_id_yearly: '',
      vendor_page_access: VENDOR_PAGE_ACCESS_OPTIONS.map((option) => option.key)
    });
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto relative my-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEdit ? 'Edit Subscription' : 'Create New Subscription'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              
              <div>
                <Input
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={errors.name}
                  placeholder="e.g., Basic, Professional, Enterprise"
                  required
                />
              </div>

              <div>
                <TextArea
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of the subscription"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Input
                    label="Price (monthly)"
                    name="price_monthly"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_monthly}
                    onChange={handleInputChange}
                    error={errors.price_monthly}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <Input
                    label="Price (yearly)"
                    name="price_yearly"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_yearly}
                    onChange={handleInputChange}
                    error={errors.price_yearly}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div>
                <Input
                  label="Currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  placeholder="USD"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Vendors pick monthly or yearly at checkout when both prices are set.
                </p>
              </div>

              <div>
                <Input
                  label="Stripe Product ID (optional)"
                  name="stripe_product_id"
                  value={formData.stripe_product_id}
                  onChange={handleInputChange}
                  placeholder="prod_..."
                />
              </div>

              <div>
                <Input
                  label="Stripe Price ID — monthly (optional)"
                  name="stripe_price_id_monthly"
                  value={formData.stripe_price_id_monthly}
                  onChange={handleInputChange}
                  error={errors.stripe_price_id_monthly}
                  placeholder="price_..."
                />
              </div>

              <div>
                <Input
                  label="Stripe Price ID — yearly (optional)"
                  name="stripe_price_id_yearly"
                  value={formData.stripe_price_id_yearly}
                  onChange={handleInputChange}
                  error={errors.stripe_price_id_yearly}
                  placeholder="price_..."
                />
              </div>
            </div>

            {/* Limits */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Limits</h3>
              
              <div>
                <Input
                  label="Max Bookings per Month"
                  name="max_bookings_per_month"
                  type="number"
                  min="0"
                  value={formData.max_bookings_per_month}
                  onChange={handleInputChange}
                  placeholder="Leave empty for unlimited"
                />
              </div>

              <div>
                <Input
                  label="Max Services"
                  name="max_services"
                  type="number"
                  min="0"
                  value={formData.max_services}
                  onChange={handleInputChange}
                  placeholder="Leave empty for unlimited"
                />
              </div>

              <div>
                <Input
                  label="Max Portfolio Items"
                  name="max_portfolio_items"
                  type="number"
                  min="0"
                  value={formData.max_portfolio_items}
                  onChange={handleInputChange}
                  placeholder="Leave empty for unlimited"
                />
              </div>

              <div>
                <Input
                  label="Max Uploaded Images"
                  name="max_images"
                  type="number"
                  min="0"
                  value={formData.max_images}
                  onChange={handleInputChange}
                  placeholder="Leave empty for unlimited"
                />
              </div>

              <div>
                <Input
                  label="Max Packages per Service"
                  name="max_packages_per_service"
                  type="number"
                  min="0"
                  value={formData.max_packages_per_service}
                  onChange={handleInputChange}
                  placeholder="Leave empty for unlimited"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Limit how many packages a vendor can attach to each service (e.g. Basic vs Deluxe).
                </p>
              </div>

              <div>
                <Input
                  label="Sort Order"
                  name="sort_order"
                  type="number"
                  min="0"
                  value={formData.sort_order}
                  onChange={handleInputChange}
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Features List */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Features List</h3>
            {formData.features_list.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 mb-2">
                <Input
                  value={feature}
                  onChange={(e) => handleFeatureChange(index, e.target.value)}
                  placeholder="Enter feature description"
                  className="flex-1"
                />
                {formData.features_list.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {errors.features_list && (
              <p className="text-red-500 text-sm mt-1">{errors.features_list}</p>
            )}
            <Button
              type="button"
              onClick={addFeature}
              variant="outline"
              className="mt-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Feature
            </Button>
          </div>

          {/* Features */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Feature Flags</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { key: 'feature_analytics', label: 'Analytics' },
                { key: 'feature_custom_branding', label: 'Custom Branding' },
                { key: 'feature_priority_support', label: 'Priority Support' },
                // { key: 'feature_api_access', label: 'API Access' },
                { key: 'feature_advanced_booking', label: 'Advanced Booking' },
                { key: 'feature_multi_location', label: 'Multi Location' }
              ].map(feature => (
                <label key={feature.key} className="flex items-center">
                  <input
                    type="checkbox"
                    name={feature.key}
                    checked={formData[feature.key]}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">{feature.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Vendor Page Access</h3>
            <p className="text-sm text-gray-500 mb-4">
              Select which vendor admin pages this plan can access.
            </p>
            <div className="space-y-4">
              {VENDOR_PAGE_ACCESS_GROUPS.map((group) => (
                <div key={group.key} className="rounded-lg border border-gray-200 p-3">
                  <p className="text-sm font-semibold text-gray-800 mb-2">{group.label}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {group.items.map((item) => (
                      <label key={item.key} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.vendor_page_access.includes(item.key)}
                          onChange={() => toggleAccess(item.key)}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="mt-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading || isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || isSubmitting}
              className="relative"
            >
              {isLoading || isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEdit ? 'Updating...' : 'Creating...'}
                </div>
              ) : (
                isEdit ? 'Update Subscription' : 'Create Subscription'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CreateSubscriptionModal;
